import { action, makeObservable, observable, runInAction } from 'mobx'

import { IThread } from '~/client/graph'
import { TagType } from '~/client/src/shared/enums/TagType'
import Delivery from '~/client/src/shared/models/Delivery'
import Message from '~/client/src/shared/models/Message'
import { ITag } from '~/client/src/shared/models/Tag'
import Thread from '~/client/src/shared/models/Thread'
import MessagesStore from '~/client/src/shared/stores/domain/MessagesStore/Messages.store'
import PhotosStore from '~/client/src/shared/stores/domain/Photos.store'
import ThreadsStore from '~/client/src/shared/stores/domain/ThreadsStore/Threads.store'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import MessageDto from '~/client/src/shared/types/MessageDto'
import BsonObjectId from '~/client/src/shared/utils/BsonObjectId'
import Guard from '~/client/src/shared/utils/Guard'
import { EMPTY_STRING } from '~/client/src/shared/utils/usefulStrings'

import { MENTION_CHAR } from '../../components/ActionBarInputWIthMentions/ActionBarInputWithMentions.store'
import Workflow from '../../models/Workflow'
import IAttachmentAdditionalInfo from '../../types/IAttachmentAdditionalInfo'
import formatBytes from '../../utils/formatBytes'
import BaseFollowingsStore from '../BaseFollowings.store'
import { FileUploadingStore } from '../domain/FileUploading.store'
import TagsStore from '../domain/Tags.store'
import UserProjectsStore from '../domain/UserProjects.store'

export default abstract class BaseActionBarStore {
  @observable public fileImage: File = null
  @observable public isImageUploading: boolean = false
  @observable public isFileUploading: boolean = false
  @observable public draftMessage = EMPTY_STRING
  @observable public shouldShowPreview = false
  @observable public uploadProgress: number = 0
  @observable public isMentionActionActive: boolean = false
  @observable public isMenuPopupShown: boolean = false
  @observable public file: File = null
  @observable public isEmojiOpened: boolean = false
  @observable public isPlusMenuShown: boolean = false

  public mentionTags: ITag[] = []
  public decodedDraftMessage: string = EMPTY_STRING

  @observable public previewImage: string = null

  protected _entityId: string = ''

  public constructor(
    protected eventsStore: EventsStore,
    protected threadsStore: ThreadsStore,
    protected messagesStore: MessagesStore,
    protected photosStore: PhotosStore,
    protected fileUploadingStore: FileUploadingStore,
    protected userProjectsStore: UserProjectsStore,
    protected tagsStore: TagsStore,
    protected baseFollowingStore?: BaseFollowingsStore,
    protected readonly attachmentAdditionalInfo?: IAttachmentAdditionalInfo,
  ) {
    makeObservable(this)
    Guard.requireAll({
      eventsStore,
      threadsStore,
      messagesStore,
      photosStore,
      fileUploadingStore,
      tagsStore,
    })
  }

  public get entityId(): string {
    return this._entityId
  }

  public set entityId(entityId: string) {
    this._entityId = entityId
  }

  @action.bound
  public setDraftMessage = (
    value: string,
    plainText: string,
    mentionTags: ITag[],
  ) => {
    this.draftMessage = value
    this.decodedDraftMessage = plainText
    this.mentionTags = [...new Set(mentionTags || [])]
  }

  @action.bound
  public hidePreview() {
    this.shouldShowPreview = false
    this.resetPreviewImage()
  }

  @action.bound
  public triggerMentionActionActivation() {
    this.draftMessage += this.draftMessage ? ` ${MENTION_CHAR}` : MENTION_CHAR
  }

  @action.bound
  public activateMentionAction() {
    this.isMentionActionActive = true
  }

  @action.bound
  public deactivateMentionAction() {
    this.isMentionActionActive = false
  }

  @action.bound
  public fullReset() {
    this.reset()
    this.closeImagePreview()
    this.resetPreviewImage()
    this.closeComposerPopups()
  }

  // The emoji and add-to-log surfaces mount inside the focused composer, so a
  // flag left true reopens them unprompted the next time it is focused.
  @action.bound
  public closeComposerPopups() {
    this.isEmojiOpened = false
    this.isPlusMenuShown = false
  }

  public get fileImageCopy() {
    return this.getFileCopy(this.fileImage)
  }

  public get isEmptyMessage(): boolean {
    return !this.draftMessage && !this.fileImage
  }

  public setPreviewImage(file: File): void {
    runInAction(() => {
      this.fileImage = file

      const reader = new FileReader()

      reader.readAsDataURL(this.fileImage)

      reader.onload = () => {
        runInAction(() => {
          this.previewImage = reader.result as string
          this.shouldShowPreview = true
        })
      }
    })
  }

  @action.bound
  public resetPreviewImage() {
    this.previewImage = null
    this.fileImage = null
  }

  @action.bound
  public resetFile() {
    this.file = null
  }

  @action.bound
  public hideMenuPopup() {
    this.isMenuPopupShown = false
  }

  @action.bound
  public showMenuPopup() {
    this.isMenuPopupShown = true
  }

  @action
  public async replyToThread(threadEntity: Message | Delivery | Workflow) {
    const fileImage = this.getFileCopy(this.fileImage)
    const file = this.getFileCopy(this.file)

    this.closeImagePreview()
    await this.subscribeMentionedUsers()

    const text = (this.decodedDraftMessage || this.draftMessage).trim()
    if (!this.fileImage && !text.length && !file) {
      return
    }

    this.reset()

    let shouldUpdateEntity = false
    if (!threadEntity.threadId) {
      const thread = await this.threadsStore.create()
      threadEntity.setThread(thread)
      shouldUpdateEntity = true
    }

    await this.postMessageWithAttachments(
      text,
      threadEntity.threadId,
      fileImage,
      file,
      threadEntity.id,
    )
    if (shouldUpdateEntity) {
      await this.updateThreadEntity(threadEntity)
    }
  }

  public closeImagePreview() {
    runInAction(() => {
      setTimeout(() => {
        runInAction(() => {
          this.shouldShowPreview = false
        })
      }, 500)
    })
  }

  @action
  public reset(): void {
    this.draftMessage = ''
    this.decodedDraftMessage = ''
  }

  public async subscribeMentionedUsers() {
    if (!this.mentionTags.length) {
      return
    }

    const userIds = []

    this.mentionTags.forEach(({ type, id }) => {
      if (type === TagType.User) {
        userIds.push(id)
      } else {
        userIds.push(...this.tagsStore.getUsersIdsByTagIds([id]))
      }
    })

    await new Promise<void>((resolve, reject) => {
      this.baseFollowingStore?.followEntities(
        [this.entityId],
        userIds,
        resolve,
        reject,
      )
    })

    this.mentionTags = []
  }

  @action.bound
  public uploadAttachment(file: File) {
    this.file = file
  }

  protected getFileCopy = (file: File) => {
    return file ? new File([file], file.name) : null
  }

  @action
  protected async postMessageWithAttachments(
    text: string,
    thread: Thread | IThread | string,
    fileImage: File,
    file: File = null,
    threadEntityId?: string,
    attachmentAdditionalInfo?: IAttachmentAdditionalInfo,
  ) {
    const {
      activeProject: { id: projectId },
    } = this.eventsStore.appState

    const message = this.messagesStore.getPreparedMessageModel(
      text || '',
      thread,
      null,
      BsonObjectId.generate(),
    )
    const attachmentInfo =
      attachmentAdditionalInfo || this.attachmentAdditionalInfo

    if (file) {
      runInAction(() => {
        this.file = null
        this.isFileUploading = true
      })

      const [result] = await this.fileUploadingStore.uploadFile(
        file,
        this.fileUploadingStore.getAttachmentFileType(file),
        projectId,
        file.name,
        false,
        attachmentInfo?.sourceEntityId,
        attachmentInfo?.sourceEntityType,
        attachmentInfo?.locationIds,
        attachmentInfo?.sourceEntityCode,
        attachmentInfo?.sourceEntityTypeName,
        attachmentInfo?.locationName,
      )
      runInAction(() => {
        this.isFileUploading = false
      })

      if (!result) {
        return
      }

      message.attachments = [
        {
          fileName: file.name,
          size: formatBytes(file.size),
          url: result.fileURL,
        },
      ]
    }

    if (fileImage) {
      this.resetPreviewImage()

      runInAction(() => {
        this.isImageUploading = true
      })

      const photoId = await this.photosStore.post(
        message,
        fileImage,
        projectId,
        fileImage.name,
        false,
        attachmentInfo?.sourceEntityId,
        attachmentInfo?.sourceEntityType,
        attachmentInfo?.locationIds,
        attachmentInfo?.sourceEntityCode,
        attachmentInfo?.sourceEntityTypeName,
        attachmentInfo?.locationName,
      )
      runInAction(() => {
        this.isImageUploading = false
      })

      if (!photoId) {
        return
      }

      message.photoId = photoId
    }

    return await this.saveMessage(threadEntityId, message)
  }

  protected abstract updateThreadEntity(
    threadEntity: Message | Delivery | Workflow,
  )

  protected saveMessage(
    _threadEntityId: string,
    message: Message,
  ): Promise<MessageDto> {
    return this.messagesStore.save(message)
  }
}
